/**
 * Nearby-spot selection for the detail page.
 *
 * Picks the closest other curated spots to the one being viewed, for a given
 * day — pure proximity, nearest first. Reads the per-day data already on each
 * EnrichedSpot; no scoring here.
 */

import { distanceKm } from "./utils";
import type { EnrichedSpot, ScoredDayFull } from "./types";

/** Max travel radius for a suggestion, km. */
export const NEARBY_MAX_KM = 50;
/** Max number of spots in the list. */
export const NEARBY_MAX = 3;

export interface NearbyItem {
  spot: EnrichedSpot;
  /** The candidate's scored day matching `selectedDay` (never a skip day). */
  day: ScoredDayFull;
  distanceKm: number;
}

export function selectNearbySpots(
  current: EnrichedSpot,
  allSpots: readonly EnrichedSpot[],
  selectedDay: number,
  opts: { maxKm?: number; max?: number } = {},
): NearbyItem[] {
  const maxKm = opts.maxKm ?? NEARBY_MAX_KM;
  const max = opts.max ?? NEARBY_MAX;

  const candidates: NearbyItem[] = [];
  for (const s of allSpots) {
    if (s.id === current.id) continue;
    const day = s.days.find((d) => d.dayOffset === selectedDay);
    if (!day || day.level === "skip") continue;
    const dist = distanceKm(current.coords, s.coords);
    if (dist > maxKm) continue;
    candidates.push({ spot: s, day, distanceKm: dist });
  }

  candidates.sort((a, b) => a.distanceKm - b.distanceKm);
  return candidates.slice(0, max);
}
